/**
 * Model editor: variables, correlation groups, intermediates, outputs.
 *
 * Variables are one flat list. Up to v1.2 they were split into "shared" and
 * "component" sections driven by a `scope` field that no engine code read — a
 * control that looked like a sampling mode and was not one (PRD §4.2). The
 * modelling error it was meant to guard against is real, but the guard has to
 * be the section hint stating the actual rule, because the split itself never
 * prevented anything: declaring T1 and T2 by hand was equally available in
 * either section.
 */

import { useState } from 'react';
import type {
  Project,
  Variable,
  CorrelationGroup,
  CorrelationPair,
  Intermediate,
  OutputSpec,
} from '../../engine/model.ts';
import { parse } from '../../engine/expr/parse.ts';
import { checkDimensions, unitCoverage } from '../../engine/units/check.ts';
import { DistributionEditor } from './DistributionEditor.tsx';
import { Info } from '../Info.tsx';
import { describeDistribution } from '../summary.ts';

interface Props {
  project: Project;
  onChange: (p: Project) => void;
}

function exprError(src: string): string | null {
  try {
    parse(src);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

function uniqueId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base}${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

export function ModelPanel({ project, onChange }: Props) {
  const [openVar, setOpenVar] = useState<string | null>(null);

  /*
   * Dimensional analysis runs on every edit (PRD §4.4). It is a tree walk over
   * expressions the user can see on one screen, so it costs nothing worth
   * memoising, and running it continuously is what makes it a spell-checker
   * rather than a lint step someone remembers to invoke.
   */
  const dimensionIssues = checkDimensions(project);
  const coverage = unitCoverage(project);
  const issuesFor = (kind: 'intermediate' | 'output', id: string) =>
    dimensionIssues.filter((d) => d.kind === kind && d.id === id);

  const allIds = new Set([
    ...project.variables.map((v) => v.id),
    ...(project.intermediates ?? []).map((m) => m.id),
  ]);

  const patch = (p: Partial<Project>) => onChange({ ...project, ...p });

  const setVariable = (index: number, v: Variable) => {
    const variables = [...project.variables];
    variables[index] = v;
    patch({ variables });
  };

  const addVariable = () => {
    const id = uniqueId('x', allIds);
    patch({
      variables: [
        ...project.variables,
        { id, distribution: { kind: 'uniform', min: 0.99, max: 1.01 } },
      ],
    });
    setOpenVar(id);
  };

  const removeVariable = (index: number) =>
    patch({ variables: project.variables.filter((_, i) => i !== index) });

  const groups = project.groups ?? [];
  const correlations = project.correlations ?? [];
  const intermediates = project.intermediates ?? [];

  const renderVariables = () => {
    const rows = project.variables.map((v, i) => ({ v, i }));

    if (rows.length === 0) {
      return <p className="muted small">No variables yet.</p>;
    }

    return (
      <ul className="var-list">
        {rows.map(({ v, i }) => (
          <li key={v.id} className={openVar === v.id ? 'var-item open' : 'var-item'}>
            <button
              type="button"
              className="var-head"
              onClick={() => setOpenVar(openVar === v.id ? null : v.id)}
              aria-expanded={openVar === v.id}
            >
              <code className="var-id">{v.id}</code>
              <span className="var-name">{v.name ?? ''}</span>
              {/* The whole point of the collapsed row: the value, without a
                  click. A ten-variable model was previously ten clicks to read. */}
              <span className="var-summary">{describeDistribution(v.distribution, v.unit)}</span>
              <span className="var-kind">{v.distribution.kind}</span>
              {v.group && <span className="tag">{v.group}</span>}
            </button>

            {openVar === v.id && (
              <div className="var-body">
                <div className="field-row">
                  <label className="field">
                    <span>Identifier</span>
                    <input
                      value={v.id}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(next)) return;
                        setVariable(i, { ...v, id: next });
                        setOpenVar(next);
                      }}
                    />
                  </label>
                  <label className="field">
                    <span>Label</span>
                    <input
                      value={v.name ?? ''}
                      onChange={(e) => setVariable(i, { ...v, name: e.target.value })}
                    />
                  </label>
                  <label className="field narrow">
                    <span>
                      Unit <Info topic="unit" />
                    </span>
                    <input
                      value={v.unit ?? ''}
                      onChange={(e) => setVariable(i, { ...v, unit: e.target.value })}
                    />
                  </label>
                </div>

                <DistributionEditor
                  value={v.distribution}
                  unit={v.unit}
                  onChange={(d) => setVariable(i, { ...v, distribution: d })}
                />

                <div className="field-row">
                  <label className="field">
                    <span>
                      Correlation group <Info topic="correlation" />
                    </span>
                    <select
                      value={v.group ?? ''}
                      onChange={(e) =>
                        setVariable(i, { ...v, group: e.target.value || undefined })
                      }
                    >
                      <option value="">Independent</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.id} (ρ = {g.rho})
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" className="danger-btn" onClick={() => removeVariable(i)}>
                    Remove
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="model-panel">
      <section>
        <h3>
          <span className="heading-text">Variables <Info topic="variable" /></span>
          <button type="button" className="add-btn" onClick={addVariable}>
            + Add
          </button>
        </h3>
        <p className="section-hint">
          Each is drawn once per trial and read by every expression that names it. One shared
          temperature is one variable; three resistors are three.
        </p>
        {renderVariables()}
        {coverage.total > 0 && coverage.recognised < coverage.total && (
          <p className="muted small unit-coverage">
            <Info topic="dimensions" /> Unit checking reads {coverage.recognised} of{' '}
            {coverage.total} variables.
            {coverage.unlabelled.length > 0 && (
              <> No unit on {coverage.unlabelled.slice(0, 4).join(', ')}
                {coverage.unlabelled.length > 4 && ` +${coverage.unlabelled.length - 4} more`}.</>
            )}
            {coverage.unrecognised.length > 0 && (
              <> Not recognised: {coverage.unrecognised.slice(0, 4).join(', ')}.</>
            )}{' '}
            Expressions involving these are skipped rather than guessed at.
          </p>
        )}
      </section>

      <section>
        <h3>
          <span className="heading-text">Correlation groups <Info topic="correlation-group" /></span>
          <button
            type="button"
            className="add-btn"
            onClick={() =>
              patch({
                groups: [
                  ...groups,
                  { id: uniqueId('lot', new Set(groups.map((g) => g.id))), rho: 0.8 },
                ],
              })
            }
          >
            + Add
          </button>
        </h3>
        <p className="section-hint">
          Parts from one reel or a matched pair track each other. ρ = 1 is perfectly
          matched; ρ = 0 is independent.
        </p>
        {groups.length === 0 ? (
          <p className="muted small">No groups — every variable is independent.</p>
        ) : (
          <ul className="plain-list">
            {groups.map((g, i) => (
              <li key={g.id} className="field-row">
                <label className="field narrow">
                  <span>Group</span>
                  <input
                    value={g.id}
                    onChange={(e) => {
                      const next = [...groups];
                      const oldId = g.id;
                      next[i] = { ...g, id: e.target.value };
                      patch({
                        groups: next,
                        variables: project.variables.map((v) =>
                          v.group === oldId ? { ...v, group: e.target.value } : v,
                        ),
                      });
                    }}
                  />
                </label>
                <label className="field">
                  <span>ρ = {g.rho.toFixed(2)}</span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={g.rho}
                    onChange={(e) => {
                      const next = [...groups];
                      next[i] = { ...g, rho: Number(e.target.value) };
                      patch({ groups: next });
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="danger-btn"
                  onClick={() =>
                    patch({
                      groups: groups.filter((_, j) => j !== i),
                      variables: project.variables.map((v) =>
                        v.group === g.id ? { ...v, group: undefined } : v,
                      ),
                    })
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3>
          <span className="heading-text">Explicit correlations <Info topic="correlation-matrix" /></span>
          <button
            type="button"
            className="add-btn"
            disabled={project.variables.length < 2}
            onClick={() => {
              const free = project.variables.filter((v) => v.group === undefined);
              if (free.length < 2) return;
              patch({
                correlations: [
                  ...correlations,
                  { a: free[0].id, b: free[1].id, rho: 0.5 },
                ],
              });
            }}
          >
            + Add
          </button>
        </h3>
        <p className="section-hint">
          For structures a group cannot express: negative correlation, or A tied to B but not
          to C. An unstated pair is independent — correlation is not inferred through a chain.
        </p>

        {correlations.length === 0 ? (
          <p className="muted small">None. Most models only need groups.</p>
        ) : (
          <>
            {correlations.map((c, i) => {
              const setPair = (patchPair: Partial<CorrelationPair>) => {
                const next = [...correlations];
                next[i] = { ...c, ...patchPair };
                patch({ correlations: next });
              };
              const eligible = project.variables.filter((v) => v.group === undefined);
              return (
                <div key={i} className="field-row">
                  <label className="field narrow">
                    <span>Variable</span>
                    <select value={c.a} onChange={(e) => setPair({ a: e.target.value })}>
                      {eligible.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field narrow">
                    <span>with</span>
                    <select value={c.b} onChange={(e) => setPair({ b: e.target.value })}>
                      {eligible.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>ρ = {c.rho.toFixed(2)}</span>
                    <input
                      type="range"
                      min={-1}
                      max={1}
                      step={0.01}
                      value={c.rho}
                      onChange={(e) => setPair({ rho: Number(e.target.value) })}
                    />
                  </label>
                  <button
                    type="button"
                    className="danger-btn"
                    onClick={() =>
                      patch({ correlations: correlations.filter((_, j) => j !== i) })
                    }
                  >
                    ×
                  </button>
                </div>
              );
            })}

            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={project.repairCorrelation ?? false}
                onChange={(e) => patch({ repairCorrelation: e.target.checked })}
              />
              <span>
                Use the nearest valid matrix if these values are inconsistent
                <em>
                  {' '}
                  — off by default, because a matrix you wrote and one the tool invented are
                  different things
                </em>
              </span>
            </label>
          </>
        )}
      </section>

      <section>
        <h3>
          <span className="heading-text">Intermediates <Info topic="intermediate" /></span>
          <button
            type="button"
            className="add-btn"
            onClick={() =>
              patch({
                intermediates: [
                  ...intermediates,
                  { id: uniqueId('step', allIds), expr: project.variables[0]?.id ?? '0' },
                ],
              })
            }
          >
            + Add
          </button>
        </h3>
        <p className="section-hint">
          Named sub-expressions, so a model reads in steps instead of one long line.
        </p>
        {intermediates.map((m, i) => {
          const err = exprError(m.expr);
          return (
            <div key={i} className="expr-row">
              <input
                className="expr-id"
                value={m.id}
                onChange={(e) => {
                  const next = [...intermediates];
                  next[i] = { ...m, id: e.target.value };
                  patch({ intermediates: next });
                }}
              />
              <span className="eq">=</span>
              <input
                className={err ? 'expr-input invalid' : 'expr-input'}
                value={m.expr}
                onChange={(e) => {
                  const next = [...intermediates];
                  next[i] = { ...m, expr: e.target.value };
                  patch({ intermediates: next });
                }}
              />
              <button
                type="button"
                className="danger-btn"
                onClick={() => patch({ intermediates: intermediates.filter((_, j) => j !== i) })}
              >
                ×
              </button>
              {err && <p className="expr-error">{err}</p>}
              {issuesFor('intermediate', m.id).map((d, k) => (
                <p key={k} className="unit-warning">
                  {d.message}
                </p>
              ))}
            </div>
          );
        })}
      </section>

      <section>
        <h3>
          <span className="heading-text">Outputs <Info topic="transfer-function" /></span>
          <button
            type="button"
            className="add-btn"
            onClick={() =>
              patch({
                outputs: [
                  ...project.outputs,
                  {
                    id: uniqueId('out', new Set(project.outputs.map((o) => o.id))),
                    expr: project.variables[0]?.id ?? '0',
                  },
                ],
              })
            }
          >
            + Add
          </button>
        </h3>
        {project.outputs.map((o, i) => {
          const err = exprError(o.expr);
          const setOutput = (patchOut: Partial<OutputSpec>) => {
            const outputs = [...project.outputs];
            outputs[i] = { ...o, ...patchOut };
            patch({ outputs });
          };
          return (
            <div key={i} className="output-card">
              <div className="expr-row">
                <input
                  className="expr-id"
                  value={o.id}
                  onChange={(e) => setOutput({ id: e.target.value })}
                />
                <span className="eq">=</span>
                <input
                  className={err ? 'expr-input invalid' : 'expr-input'}
                  value={o.expr}
                  onChange={(e) => setOutput({ expr: e.target.value })}
                />
                {project.outputs.length > 1 && (
                  <button
                    type="button"
                    className="danger-btn"
                    onClick={() => patch({ outputs: project.outputs.filter((_, j) => j !== i) })}
                  >
                    ×
                  </button>
                )}
              </div>
              {err && <p className="expr-error">{err}</p>}
              {issuesFor('output', o.id).map((d, k) => (
                <p key={k} className="unit-warning">
                  {d.message}
                </p>
              ))}
              <div className="field-row">
                <label className="field narrow">
                  <span>
                    Unit <Info topic="unit" />
                  </span>
                  <input
                    value={o.unit ?? ''}
                    onChange={(e) => setOutput({ unit: e.target.value })}
                  />
                </label>
                <label className="field narrow">
                  <span>
                    LSL <Info topic="spec-limits" />
                  </span>
                  <input
                    type="number"
                    step="any"
                    value={o.lsl ?? ''}
                    onChange={(e) =>
                      setOutput({ lsl: e.target.value === '' ? undefined : Number(e.target.value) })
                    }
                  />
                </label>
                <label className="field narrow">
                  <span>Target</span>
                  <input
                    type="number"
                    step="any"
                    value={o.target ?? ''}
                    onChange={(e) =>
                      setOutput({
                        target: e.target.value === '' ? undefined : Number(e.target.value),
                      })
                    }
                  />
                </label>
                <label className="field narrow">
                  <span>
                    USL <Info topic="spec-limits" />
                  </span>
                  <input
                    type="number"
                    step="any"
                    value={o.usl ?? ''}
                    onChange={(e) =>
                      setOutput({ usl: e.target.value === '' ? undefined : Number(e.target.value) })
                    }
                  />
                </label>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
